import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Dropdown from "../../../../parts/dropdown";
import SvgIcon from "../../../../parts/SvgIcon";
import {
  toApiSteps,
  updateStepConfig,
} from "../../../../utils/automation-builder";
import { optionsOf } from "../modals/automation-filter";
import { valueNames } from "./FilterChips";
import { saveChanges } from "./save-changes";

// One field of the filter: what the people who continue have to match ("Any" lets everyone in)
const FieldSelect = ({ field, options, values, onChange }) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key, params) => t(`emailAutomations.builder.${key}`, params);
  const choices = optionsOf(field, options);
  // The only location is everyone's: nothing to choose
  if (field.optionsSource === "locations" && choices.length <= 1) return null;
  const names = new Map(
    choices.map((choice) => [
      String(choice.id),
      valueNames(field.key, [choice.id], options, t)[0] ?? choice.name,
    ])
  );
  const label = t(`emailAutomations.builder.filterModal.titles.${field.key}`, {
    defaultValue: tr("filter.is", {
      field: tr(`filter.fields.${field.key}`),
    }),
  });
  const any = tr("filterModal.any");
  const summary =
    values.length > 0
      ? values.map((value) => names.get(String(value)) ?? value).join(", ")
      : any;

  return (
    <div className="fp-field">
      <span>{label}</span>
      <div className="ep-select">
        <Dropdown
          overlay
          summaryText={summary}
          selectedValue={
            values.length <= 1 ? String(values[0] ?? "") : undefined
          }
          options={[
            { text: any, value: "" },
            ...choices.map((choice) => ({
              text: names.get(String(choice.id)),
              value: String(choice.id),
            })),
          ]}
          onSelectFn={(value) =>
            onChange(
              value === ""
                ? []
                : [choices.find((choice) => String(choice.id) === value).id]
            )
          }
        />
      </div>
    </div>
  );
};

FieldSelect.propTypes = {
  field: PropTypes.object.isRequired,
  options: PropTypes.object,
  values: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

// Editing a Filter step: who continues past it, and what happens to the rest
const FilterPanel = ({
  automationId,
  steps,
  index,
  definition,
  options,
  closeHref,
}) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key) => t(`emailAutomations.builder.filterPanel.${key}`);
  const [filters, setFilters] = useState(steps[index].config.filters ?? {});

  const setField = (key, values) =>
    setFilters((current) => {
      const next = { ...current, [key]: values };
      if (values.length === 0) delete next[key];
      return next;
    });

  const save = () =>
    saveChanges(automationId, {
      steps: updateStepConfig(toApiSteps(steps), index, { filters }),
    });

  return (
    <div className="ab-panel fp">
      <h3>{tr("title")}</h3>
      <p className="ab-panel__sub">{tr("intro")}</p>

      {definition.filterFields.map((field) => (
        <FieldSelect
          key={field.key}
          field={field}
          options={options}
          values={filters[field.key] ?? []}
          onChange={(values) => setField(field.key, values)}
        />
      ))}

      <p className="fp-note">
        <SvgIcon
          src="/img/new-icons/info-circle.svg"
          useCurrentColor
          classNames="size-5 shrink-0"
          aria-hidden="true"
        />
        <span>{tr("note")}</span>
      </p>

      <div className="ab-panel__actions">
        {/* biome-ignore lint/a11y/useSemanticElements: a real link so Rill navigates; role only picks up button styles */}
        <a
          href={closeHref}
          className="ep-cancel"
          data-button-type="secondary"
          role="button"
        >
          {tr("cancel")}
        </a>
        <button type="button" data-button-type="primary" onClick={save}>
          {tr("save")}
        </button>
      </div>
    </div>
  );
};

FilterPanel.propTypes = {
  automationId: PropTypes.string.isRequired,
  steps: PropTypes.array.isRequired,
  index: PropTypes.number.isRequired,
  definition: PropTypes.object.isRequired,
  options: PropTypes.object,
  closeHref: PropTypes.string.isRequired,
};

export default FilterPanel;
